/**
 * ★ Fase 79 — Landing THREE GARDEN QERRET (IT-AL-TG) · URL: /f/it-al-tg
 *
 * Ricostruita dal mockup cliente «Landing page immobiliare Three Garden Qerret»
 * (testi copiati fedelmente). Render HD forniti dal cliente in
 * /public/funnels/three-garden/*.webp.
 *
 * Flusso CTA (★ Fase 94 · Doc 02 — form progressivo PRIMA di Calendly):
 *   «Prenota la Zoom strategica per valutare Three Garden» → DIALOG 3 step
 *   1) contatti (nome / email / telefono) → lead nel CRM (funnel IT-AL-TG) + brochure via email
 *   2) prequalifica (obiettivo / budget / orizzonte) → evento `prequal_completed`
 *   3) conferma → REDIRECT Calendly (booking_url live dal CRM) con prefill + UTM.
 *   Bridge esplicito: Zoom → Verification Pass Albania (/f/it#verification).
 *
 * Doc 02 — TODO espliciti (asset non forniti dal cliente, NON inventati):
 *   • TG_MINI_VIDEO: mini video 45s Three Garden (ID YouTube) → lo slot resta nascosto finché vuoto.
 *   • Data/versione brochure e date dei video testimonianze (non disponibili come metadata).
 *
 * La landing Narea (/f/it-al-na) resta in AlbaniaAssetLanding.jsx, intatta.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';
import { api } from '../../../lib/api';
import { TestimonialsSection } from '../../../components/funnels/TestimonialsSection';
import { setPageMeta, ogImageFor } from '../../../lib/seo';
// ★ Fase 94 (Doc 02) — attribution, tracking consent-gated, dialog 3 step, sezioni agenda/prove/bridge
import { captureAttribution, trackFunnelEvent } from '../../../lib/attribution';
import { ZoomLeadDialog } from '../../../components/funnels/ZoomLeadDialog';
import { ZoomAgendaSection, ProofsSection, VerificationPassBridge, useVpDates } from '../../../components/funnels/Doc02Sections';
import { NoObligationNote } from '../../../components/funnels/NoObligationNote';
import { VpRecapVideo } from '../../../components/funnels/VpRecapVideo';
import { HCF_TESTIMONIALS } from '../../../lib/testimonials';
import {
  ArrowRight, Play, Waves, Building2, TrendingUp, MapPin, Check, Home, Settings,
  BarChart3, ShieldCheck, UserRound, Search, ListChecks, Info, Sofa, FileText,
  CheckCircle2, Menu, X, Video, CalendarClock, Droplets, MessageSquare,
} from 'lucide-react';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../../../components/ui/accordion';

/* ── Design tokens (campionati dal mockup) ── */
const IVORY = '#F9F6F0';
const IVORY_2 = '#F1ECE2';
const PAPER = '#FFFDF9';
const INK = '#1A2318';
const INK_SOFT = '#59615B';
const LINE = '#E3DCCF';
const OLIVE = '#3C5330';
const OLIVE_DEEP = '#2F4226';
const SAGE = '#EBEEE3';

/* Fallback: il valore live arriva da GET /public/funnel/<slug> (booking_url). */
const CALENDLY_CEO = 'https://calendly.com/legal-housecashflows/colloquio-strategico-con-il-ceo-di-hcf-group';

/* ★ Fase 94 (Doc 02) — TODO cliente: mini video 45s Three Garden (ID YouTube per lingua).
   Finché è null lo slot video NON viene renderizzato (nessun placeholder finto in pagina). */
const TG_MINI_VIDEO = { it: null, es: null, en: null };

/* ★ Fase 94 — palette condivisa con dialog/sezioni Doc 02 (stessi token del mockup, nessun cambio brand) */
const TOKENS = {
  bg: IVORY, bg2: IVORY_2, paper: PAPER, ink: INK, inkSoft: INK_SOFT,
  accent: OLIVE, accentDeep: OLIVE_DEEP, line: LINE, soft: SAGE, onAccent: '#FFFBF3',
};

/* ── Stili locali (palette mockup: verde oliva + avorio) ──
   Solo transform/opacity/colori nelle transizioni (niente `transition: all`). */
const CSS = `
  .tgl-btn-primary { background: ${OLIVE}; color: #FFFBF3; box-shadow: 0 10px 24px rgba(60,83,48,0.22), inset 0 1px 0 rgba(255,255,255,0.12); transition: transform .2s, box-shadow .2s, background-color .2s, opacity .2s; }
  .tgl-btn-primary:hover:not(:disabled) { background: ${OLIVE_DEEP}; transform: translateY(-1px); box-shadow: 0 14px 30px rgba(60,83,48,0.30), inset 0 1px 0 rgba(255,255,255,0.14); }
  .tgl-btn-primary:active:not(:disabled) { transform: translateY(0); }
  .tgl-btn-primary:focus-visible { outline: 2px solid ${OLIVE}; outline-offset: 3px; }
  .tgl-btn-primary:disabled { opacity: .5; cursor: not-allowed; }
  .tgl-btn-outline { background: transparent; color: ${INK}; border: 1px solid ${LINE}; transition: border-color .2s, background-color .2s, transform .2s; }
  .tgl-btn-outline:hover { border-color: ${OLIVE}; background: rgba(60,83,48,0.06); }
  .tgl-btn-outline:active { transform: scale(0.99); }
  .tgl-btn-outline:focus-visible { outline: 2px solid ${OLIVE}; outline-offset: 3px; }
  .tgl-nav-link { color: ${INK_SOFT}; position: relative; padding: 4px 0; transition: color .15s; }
  .tgl-nav-link::after { content: ''; position: absolute; left: 0; right: 0; bottom: -2px; height: 1.5px; background: ${OLIVE}; transform: scaleX(0); transform-origin: left; transition: transform .2s; }
  .tgl-nav-link:hover, .tgl-nav-link:focus-visible { color: ${INK}; outline: none; }
  .tgl-nav-link:hover::after, .tgl-nav-link:focus-visible::after { transform: scaleX(1); }
  .tgl-card { background: ${PAPER}; border: 1px solid ${LINE}; box-shadow: 0 1px 2px rgba(26,35,24,0.04); transition: transform .2s, box-shadow .2s; }
  .tgl-card:hover { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(26,35,24,0.10); }
  .tgl-tile img { transition: transform .5s; }
  .tgl-tile:hover img { transform: scale(1.04); }
  .tgl-arrow { width: 2.5rem; height: 2.5rem; border-radius: 9999px; display: grid; place-items: center; background: ${OLIVE}; color: #FFFBF3; box-shadow: 0 6px 16px rgba(60,83,48,0.28); transition: transform .2s, background-color .2s; }
  .tgl-arrow:hover { background: ${OLIVE_DEEP}; transform: translateX(2px); }
  .tgl-arrow:active { transform: translateX(0) scale(0.97); }
  .tgl-arrow:focus-visible { outline: 2px solid ${OLIVE}; outline-offset: 3px; }
  .tgl-field { background: #FFFFFF; border: 1px solid ${LINE}; color: ${INK}; caret-color: ${OLIVE}; transition: border-color .2s, box-shadow .2s; }
  .tgl-field::placeholder { color: #8B9088; }
  .tgl-field:hover:not(:focus) { border-color: #C9C0AF; }
  .tgl-field:focus { border-color: ${OLIVE}; box-shadow: 0 0 0 3px rgba(60,83,48,0.16); outline: none; }
  .tgl-field:invalid:not(:placeholder-shown) { border-color: #B4533F; }
  .tgl-accordion-item { background: ${PAPER}; border: 1px solid ${LINE} !important; border-radius: 1rem; padding: 0 1.25rem; transition: border-color .2s; }
  .tgl-accordion-item[data-state="open"] { border-color: ${OLIVE} !important; }
  .tgl-grain { position: absolute; inset: 0; opacity: .05; pointer-events: none; mix-blend-mode: overlay; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); }
  .three-garden-landing ::selection { background: rgba(60,83,48,0.22); }
  @media (prefers-reduced-motion: reduce) {
    .tgl-btn-primary, .tgl-btn-outline, .tgl-card, .tgl-tile img, .tgl-arrow, .tgl-nav-link::after { transition: none; }
  }
`;

const IMG = (n) => `/funnels/three-garden/${n}.webp`;
const PIC = {
  hero: IMG('tg-beach'),
  boulevard: IMG('tg-boulevard'),
  terrace: IMG('tg-terrace-sunset'),
  rooftop: IMG('tg-rooftop-pool'),
  towers: IMG('tg-towers-sunset'),
  aerialPools: IMG('tg-aerial-pools'),
  aerialSunset: IMG('tg-aerial-sunset'),
  night: IMG('tg-night'),
  facade: IMG('tg-facade'),
  pool: IMG('tg-pool-panoramic'),
};

/* ── Contenuti per lingua (IT = testi del mockup · ★ Fase 80 ES = traduzione) ── */
const CONTENT = {
  it: {
    slug: 'it-al-tg', code: 'IT-AL-TG', homeHref: '/f/it', propertySlug: 'three-garden-qerret',
    docTitle: 'Three Garden Qerret · Albania — HCF Property',
    docDescription: 'Three Garden Qerret: fronte mare in Albania, tre torri con accesso diretto alla spiaggia e rooftop pool. Prenota la Zoom Strategica con HCF Property.',
    leadMessage: 'Richiesta Zoom Strategica — Three Garden (Qerret, Albania) · landing IT-AL-TG',
    nav: [
      { id: 'progetto', label: 'Three Garden' },
      { id: 'posizione', label: 'Posizione' },
      { id: 'agenda', label: 'Agenda Zoom' },
      { id: 'percorso', label: 'Dopo la Zoom' },
      { id: 'infrastruttura', label: 'Infrastruttura patrimoniale' },
      { id: 'faq', label: 'Domande frequenti' },
    ],
    menuOpen: 'Apri menu', menuClose: 'Chiudi menu', sections: 'Sezioni',
    cta: 'Prenota la Zoom Strategica', ctaShort: 'Prenota la Zoom', ctaAria: 'Prenota la Zoom Strategica',
    // ★ Fase 94 (Doc 02) — CTA primaria testuale richiesta (coerente con lo step reale: form → Zoom)
    ctaPrimary: 'Prenota la Zoom strategica per valutare Three Garden',
    heroKicker: ['Albania', 'Qerret'],
    heroTitle: 'Three Garden Qerret',
    heroSubtitle: ['Non partire dall’immobile.', 'Parti dall’infrastruttura patrimoniale.'],
    heroLead: 'Scopri se questo progetto è adatto al tuo profilo, al tuo obiettivo e alla tua struttura prima di fare qualunque scelta.',
    heroText: 'Three Garden non è solo un progetto fronte mare. Per HCF è un possibile tassello di un’infrastruttura patrimoniale costruita con logica, flussi, protezione, gestione e visione.',
    heroSecondary: 'Guarda il progetto',
    heroImageAlt: 'Three Garden Qerret — vista dalla spiaggia',
    heroFeatures: [
      { icon: Waves, t: 'Fronte mare', d: 'Accesso diretto alla spiaggia' },
      { icon: Droplets, t: 'Rooftop pool panoramiche', d: 'Vista unica sulla costa' },
      { icon: Building2, t: 'Architettura contemporanea', d: 'Design moderno e identità iconica' },
    ],
    strip: 'Per chi vuole capire prima struttura, rischio, gestione e potenziale. Non per curiosi.',
    steps: [
      { n: '01', icon: TrendingUp, t: 'Analizzi il tuo punto di partenza', d: 'Capisci se ti serve davvero un asset, quale ruolo può avere e con quale struttura.' },
      { n: '02', icon: MapPin, t: 'Valuti il progetto con contesto reale', d: 'Posizione, qualità, destinazione d’uso, gestione e logica patrimoniale.' },
      { n: '03', icon: Check, t: 'Entri solo se ha senso per te', d: 'Prima la strategia, poi la visita, poi la decisione.' },
    ],
    projectCards: [
      { img: PIC.boulevard, pos: 'center 55%', t: 'Architettura contemporanea', d: 'Tre torri, identità forte e presenza iconica.' },
      { img: PIC.terrace, pos: 'center 60%', t: 'Spazi che elevano l’esperienza', d: 'Aree comuni curate, linee pulite e comfort quotidiano.' },
      { img: PIC.rooftop, pos: 'center 45%', t: 'Rooftop con vista mare', d: 'Piscine panoramiche e lifestyle premium.' },
      { img: PIC.towers, pos: 'center 55%', t: 'Un progetto sul mare', d: 'Posizione costiera, estetica e vivibilità.' },
    ],
    infraTitle: 'Perché partire da un’infrastruttura patrimoniale?',
    infraSubtitle: 'Un immobile da solo non basta. Il vero valore nasce da una visione più ampia.',
    infraImageAlt: 'Three Garden Qerret — vista aerea',
    infraPillars: [
      { icon: Home, t: 'Asset', d: 'Il progetto è solo una parte dell’equazione.' },
      { icon: Settings, t: 'Gestione', d: 'Senza un operatore e una logica di utilizzo, il mattone da solo non basta.' },
      { icon: BarChart3, t: 'Fiscalità e flussi', d: 'Struttura, movimenti e impostazione incidono sul risultato finale.' },
      { icon: ShieldCheck, t: 'Protezione e visione', d: 'Patrimonializzazione, continuità e ordine contano quanto la scelta del bene.' },
    ],
    zoomTitle: 'Cosa ottieni nella Zoom Strategica?',
    zoomSubtitle: 'Un confronto iniziale per capire se Three Garden può essere un tassello coerente nella tua infrastruttura patrimoniale.',
    zoomCards: [
      { icon: UserRound, t: 'Analisi del tuo obiettivo', d: 'Capire cosa vuoi costruire davvero.', img: PIC.night, pos: 'center 45%' },
      { icon: Search, t: 'Valutazione del progetto', d: 'Leggere il progetto nel modo corretto, non solo esteticamente.', img: PIC.facade, pos: 'center 40%' },
      { icon: MapPin, t: 'Contesto territoriale', d: 'Qerret, costa albanese, potenziale e logica d’uso.', img: PIC.aerialSunset, pos: 'center 55%' },
      { icon: ListChecks, t: 'Prossimi passi', d: 'Se ha senso si passa al funnel successivo e alla visita.', img: PIC.boulevard, pos: 'center 70%' },
    ],
    zoomNoteB: 'La Zoom non è una chiamata generica:', zoomNote: 'serve a filtrare, orientare e capire se proseguire.',
    whyTitle: ['Perché Three Garden', 'Perché Qerret'],
    whyText: 'Una posizione fronte mare in una delle aree più interessanti della costa albanese. Qerret unisce mare, tranquillità e uno sviluppo costante, con un contesto moderno e un lifestyle contemporaneo. Three Garden offre qualità costruttiva, architettura distintiva e un’esperienza abitativa pensata per durare nel tempo.',
    whyTiles: [
      { img: PIC.hero, pos: 'center 70%', t: 'Vivere il mare ogni giorno', d: 'Spiaggia, orizzonte e luce naturale' },
      { img: PIC.pool, pos: 'center 60%', t: 'Rooftop con vista infinita', d: 'Relax, benessere e connessione' },
      { img: PIC.aerialPools, pos: 'center 32%', t: 'Un territorio in crescita', d: 'Natura, sviluppo e potenziale' },
      { img: PIC.night, pos: 'center 50%', t: 'Uno stile di vita contemporaneo', d: 'Qualità, design e vivibilità' },
    ],
    lifestyle: [
      { icon: Waves, t: 'Vista mare', d: 'Accesso diretto alla spiaggia' },
      { icon: Droplets, t: 'Piscine panoramiche', d: 'Rooftop con vista a 360°' },
      { icon: BarChart3, t: 'Design premium', d: 'Architettura moderna e materiali di qualità' },
      { icon: Sofa, t: 'Terrazze ampie', d: 'Spazi esterni da vivere tutto l’anno' },
      { icon: MapPin, t: 'Posizione costiera', d: 'Tranquillità, servizi e collegamenti internazionali' },
    ],
    finalTitle: ['La scelta giusta non parte dal fascino.', 'Parte dalla struttura.'],
    finalText: 'Se vuoi capire se Three Garden ha davvero senso per il tuo percorso, entra prima nella Zoom Strategica. Da lì capisci se e come proseguire.',
    finalPoints: [
      { icon: UserRound, t: 'Analisi iniziale personalizzata', d: 'sul tuo profilo e obiettivi' },
      { icon: FileText, t: 'Contesto reale, non promesse', d: 'Dati, scenari e opportunità concrete' },
      { icon: CheckCircle2, t: 'Accesso al percorso successivo solo se coerente', d: 'Nessuna pressione, solo metodo' },
    ],
    faqKicker: 'Risposte alle tue domande', faqTitle: 'Domande frequenti',
    faqIntro: 'Le risposte alle domande più comuni per aiutarti a fare una scelta consapevole.',
    faq: [
      { q: 'Cos’è esattamente la Zoom Strategica?', a: 'È un confronto iniziale con il CEO di HCF Group: analizziamo il tuo obiettivo, leggiamo Three Garden nel contesto reale di Qerret e capiamo se può essere un tassello coerente nella tua infrastruttura patrimoniale. Non è una vendita: serve a filtrare, orientare e decidere se proseguire.' },
      { q: 'La Zoom mi obbliga all’acquisto?', a: 'No. Nessun obbligo di acquisto. La Zoom Strategica serve a decidere con dati e contesto reale, non con rendering o promesse. Si passa alla visita e ai passi successivi solo se ha senso per te.' },
      { q: 'Perché partire dalla strategia e non dall’immobile?', a: 'Perché il valore reale nasce da posizione, domanda, qualità dell’asset, gestione e struttura: elementi da leggere prima di qualsiasi decisione. Prima la strategia, poi la visita, poi la decisione.' },
      { q: 'Cosa significa infrastruttura patrimoniale?', a: 'Un sistema integrato di asset, governance, gestione e visione di lungo periodo che lavora nel tempo per il tuo patrimonio. Non la singola unità, ma il modo in cui si inserisce in una strategia.' },
      { q: 'Perché Qerret oggi?', a: 'Fronte mare, accessibilità, domanda in aumento e una finestra di posizionamento ancora aperta: un contesto da valutare con anticipo e metodo, prima che il mercato maturi ulteriormente.' },
      { q: 'Ricevo la brochure di Three Garden?', a: 'Sì. Compilando il form ricevi subito via email la brochure ufficiale di Three Garden e vieni portato alla scelta di data e ora della Zoom Strategica con il CEO.' },
      // ★ Fase 94 (Doc 02) — FAQ su costi / gestione / tempi / dopo la Zoom (nessuna cifra inventata)
      { q: 'Quali costi devo considerare oltre al prezzo dell’unità?', a: 'Nella Zoom analizziamo insieme la struttura completa: prezzo dell’unità, imposte e costi notarili locali, eventuali costi di gestione e di arredo. Non pubblichiamo cifre generiche: ogni voce viene letta sul dossier aggiornato del progetto e sul tuo profilo. La Zoom Strategica e la brochure sono gratuite.' },
      { q: 'Come funziona la gestione dell’immobile se non vivo in Albania?', a: 'Per HCF il mattone da solo non basta: la gestione è parte dell’infrastruttura patrimoniale. Nella Zoom valutiamo la logica di utilizzo (uso personale, locazione, misto) e le opzioni di gestione disponibili per Three Garden, con costi e condizioni da verificare sul dossier e dal vivo durante il Verification Pass.' },
      { q: 'Quali sono i tempi del percorso?', a: 'Zoom Strategica: prenotabile subito, dura 30 minuti. Verification Pass Albania: weekend programmati con date reali, aggiornate dal nostro sistema (le trovi in questa pagina). Tempi di consegna e stato di avanzamento di Three Garden: li verifichi sul dossier e sul posto, non su una promessa.' },
      { q: 'Cosa succede dopo la Zoom?', a: 'Se Three Garden è coerente con il tuo profilo, il passo successivo è il Verification Pass Albania: un weekend sul posto per vedere territorio, progetto e dati dal vivo con il team HCF (Private Rate €300). Se non è coerente, te lo diciamo chiaramente. Nessun obbligo di acquisto; condizioni e disponibilità soggette a verifica.' },
    ],
    footerPrivacy: 'Privacy', footerCookies: 'Cookie', footerSections: 'Sezioni (footer)',
    brochureEmail: true,
    // ★ Fase 94 (Doc 02) — Agenda della Zoom (30 min), client-facing, coerente con HCF Senior Zoom System
    agenda: {
      kicker: 'Agenda della Zoom Strategica',
      title: 'Cosa succede in 30 minuti',
      subtitle: 'Un confronto strutturato, non una chiamata di vendita. Sai in anticipo come useremo il tempo.',
      meta: '30 minuti · su Zoom · con il CEO di HCF Group',
      items: [
        { time: '0–5 min', t: 'Il tuo punto di partenza', d: 'Profilo, obiettivo e struttura: capiamo se e che ruolo può avere un asset come Three Garden nel tuo percorso.' },
        { time: '5–15 min', t: 'Three Garden nel contesto reale', d: 'Posizione a Qerret, qualità costruttiva, destinazione d’uso, logica di gestione e patrimoniale.' },
        { time: '15–25 min', t: 'Struttura, costi e gestione', d: 'Cosa considerare davvero: struttura d’acquisto, costi accessori, gestione e flussi. Numeri solo da dossier verificabile.' },
        { time: '25–30 min', t: 'Prossimi passi, solo se coerenti', d: 'Se ha senso per te: accesso al Verification Pass Albania (verifica sul posto). Altrimenti ci si saluta, senza pressione.' },
      ],
      note: 'La Zoom è gratuita. Porta le tue domande su costi, gestione e tempi: risposte documentate, non slide.',
    },
    video: { kicker: 'Mini video · 45 secondi', title: 'Three Garden in 45 secondi', text: 'Il progetto, la posizione e il metodo HCF in meno di un minuto.' },
    // ★ Fase 94 (Doc 02) — Prove verificabili (solo fonti reali; date live dal CRM)
    proofs: {
      kicker: 'Prove, non promesse',
      title: 'Cosa puoi verificare prima di decidere',
      intro: 'Ogni affermazione di questa pagina è collegata a qualcosa che puoi leggere, guardare o vedere di persona.',
      items: [
        { icon: FileText, t: 'Brochure ufficiale Three Garden Residence', d: 'Il documento del progetto: piante, render e dotazioni. La ricevi via email compilando il form, prima della Zoom.', meta: 'PDF · inviata automaticamente' },
        { icon: Video, t: (n) => `${n} testimonianze video`, d: 'Clienti e partecipanti al Verification Pass Albania raccontano cosa hanno visto, dal vivo.', meta: 'YouTube · in questa pagina', action: 'testimonianze', actionLabel: 'Guarda le testimonianze' },
        { icon: CalendarClock, t: 'Date reali del Verification Pass Albania', d: 'Weekend di verifica sul posto con il team HCF. Date e posti aggiornati in tempo reale dal nostro sistema.', dates: true },
        { icon: MessageSquare, t: 'Confronto diretto con il CEO', d: 'Nella Zoom puoi fare qualsiasi domanda su costi, gestione e tempi: risposte documentate, non slide.', meta: '30 minuti · Zoom' },
      ],
      datesEmpty: 'Nuove date in programmazione: le comunichiamo nella Zoom.',
      seatsLabel: 'posti', soldOut: 'Completo',
      footnote: 'Le date e i posti mostrati sono letti in tempo reale dal nostro sistema. Nessun obbligo di acquisto; condizioni e disponibilità soggette a verifica.',
    },
    // ★ Fase 94 (Doc 02) — Bridge esplicito Zoom → Verification Pass Albania (destinazione configurabile)
    bridge: {
      kicker: 'Dopo la Zoom',
      title: ['Il passo successivo è la verifica sul posto:', 'il Verification Pass Albania.'],
      text: 'La Zoom serve a capire se Three Garden può avere senso per te. Se la risposta è sì, il percorso continua con il Verification Pass: un weekend in Albania per vedere territorio, progetto e dati dal vivo, con il team HCF.',
      steps: [
        { icon: Video, t: 'Zoom Strategica · 30 min', d: 'Profilo, obiettivo e lettura di Three Garden nel contesto reale. Gratuita e senza obbligo.', tag: 'Oggi' },
        { icon: MapPin, t: 'Verification Pass Albania', d: 'Weekend sul posto: territorio, Three Garden e gli asset selezionati, confronto con il team HCF.', tag: 'Private Rate €300 · weekend' },
        { icon: CheckCircle2, t: 'Decisione con dati verificati', d: 'Solo dopo aver visto di persona. Prima la strategia, poi la visita, poi la decisione.', tag: 'Nessuna pressione' },
      ],
      includesTitle: 'Cosa include il Verification Pass',
      includes: ['Accesso completo ai dati e alla documentazione degli asset', 'Analisi del territorio dal vivo con il team HCF', 'Visita on-site di Three Garden e delle opportunità selezionate', 'Confronto diretto su costi, gestione e tempi'],
      includesNote: 'Cosa include e cosa non include il Pass (viaggio, pernottamento, condizioni) viene chiarito nella Zoom e nella pagina del Verification Pass.',
      datesTitle: 'Prossime date', datesEmpty: 'Nuove date in programmazione.', datesNote: 'Date e posti aggiornati in tempo reale dal nostro sistema.',
      seatsLabel: 'posti', soldOut: 'Completo',
      price: 'Private Rate €300',
      cta: 'Scopri il Verification Pass Albania',
      href: '/f/it#verification',       // destinazione esplicita e configurabile (hub IT, sezione Verification Pass)
      vpSource: 'it-al',                 // date live: GET /api/public/funnel/it-al → IT-AL
    },
    dlg: {
      kicker: 'Zoom Strategica · Three Garden Qerret',
      title: 'Prenota la Zoom Strategica.',
      descBrochure: <>Lascia i tuoi contatti: ricevi <b>subito via email la brochure Three Garden</b> e passi alla scelta di data e ora della Zoom con il <b>CEO di HCF Group</b>.</>,
      desc: <>Lascia i tuoi contatti: subito dopo passi alla scelta di data e ora della Zoom Strategica con il <b>team HCF</b>.</>,
      phName: 'Nome e cognome *', phEmail: 'Email *', phPhone: 'Telefono (consigliato)',
      submit: 'Continua',
      errName: 'Inserisci nome e cognome (almeno 2 caratteri).', errEmail: 'Inserisci un indirizzo email valido.',
      legal: 'Inviando accetti la nostra', privacy: 'Privacy Policy',
      steps: ['Contatti', 'Profilo', 'Calendario'],
      pq: {
        kicker: 'Prequalifica · 20 secondi',
        title: 'Tre risposte per preparare una Zoom utile.',
        desc: 'Servono solo a leggere Three Garden sul tuo profilo reale. Nessuna risposta è vincolante.',
        goal: 'Obiettivo principale', budget: 'Budget indicativo', timeline: 'Orizzonte decisionale',
        submit: 'Continua alla prenotazione', back: 'Indietro', skip: 'Preferisco rispondere in Zoom',
        note: 'Nessun obbligo di acquisto; condizioni e disponibilità soggette a verifica.',
      },
      okTitle: 'Richiesta ricevuta',
      okBrochure: <>La <b>brochure Three Garden</b> è in arrivo nella tua casella email. Tra un istante ti portiamo al calendario per scegliere <b>data e ora della Zoom Strategica</b> con il CEO di HCF Group.</>,
      ok: <>Tra un istante ti portiamo al calendario per scegliere <b>data e ora della Zoom Strategica</b> con il team HCF.</>,
      okCta: 'Scegli data e ora della Zoom', okNote: 'Reindirizzamento automatico in corso…',
      bridge: { title: 'Dopo la Zoom: Verification Pass Albania', text: 'Se Three Garden è coerente con il tuo profilo, il passo successivo è la verifica sul posto (weekend · Private Rate €300).', cta: 'Scopri il Verification Pass' },
      toastTitle: 'Richiesta ricevuta!',
      toastBrochure: 'Brochure in arrivo via email. Ti portiamo alla scelta di data e ora della Zoom con il CEO.',
      toast: 'Ti portiamo alla scelta di data e ora della Zoom Strategica.',
      toastErr: 'Invio non riuscito, riprova.',
    },
  },
  es: {
    slug: 'es-al-tg', code: 'ES-AL-TG', homeHref: '/f/es', propertySlug: 'es-three-garden-qerret',
    docTitle: 'Three Garden Qerret · Albania — HCF Property',
    docDescription: 'Three Garden Qerret: frente al mar en Albania, tres torres con acceso directo a la playa y rooftop pools. Reserva la Zoom Estratégica con HCF Property.',
    leadMessage: 'Solicitud Zoom Estratégica — Three Garden (Qerret, Albania) · landing ES-AL-TG',
    nav: [
      { id: 'progetto', label: 'Three Garden' },
      { id: 'posizione', label: 'Ubicación' },
      { id: 'agenda', label: 'Agenda Zoom' },
      { id: 'percorso', label: 'Después de la Zoom' },
      { id: 'infrastruttura', label: 'Infraestructura patrimonial' },
      { id: 'faq', label: 'Preguntas frecuentes' },
    ],
    menuOpen: 'Abrir menú', menuClose: 'Cerrar menú', sections: 'Secciones',
    cta: 'Reserva la Zoom Estratégica', ctaShort: 'Reserva la Zoom', ctaAria: 'Reserva la Zoom Estratégica',
    ctaPrimary: 'Reserva la Zoom estratégica para evaluar Three Garden',
    heroKicker: ['Albania', 'Qerret'],
    heroTitle: 'Three Garden Qerret',
    heroSubtitle: ['No partas del inmueble.', 'Parte de la infraestructura patrimonial.'],
    heroLead: 'Descubre si este proyecto encaja con tu perfil, tu objetivo y tu estructura antes de tomar cualquier decisión.',
    heroText: 'Three Garden no es solo un proyecto frente al mar. Para HCF es una posible pieza de una infraestructura patrimonial construida con lógica, flujos, protección, gestión y visión.',
    heroSecondary: 'Mira el proyecto',
    heroImageAlt: 'Three Garden Qerret — vista desde la playa',
    heroFeatures: [
      { icon: Waves, t: 'Frente al mar', d: 'Acceso directo a la playa' },
      { icon: Droplets, t: 'Rooftop pools panorámicas', d: 'Vistas únicas a la costa' },
      { icon: Building2, t: 'Arquitectura contemporánea', d: 'Diseño moderno e identidad icónica' },
    ],
    strip: 'Para quien quiere entender antes estructura, riesgo, gestión y potencial. No para curiosos.',
    steps: [
      { n: '01', icon: TrendingUp, t: 'Analizas tu punto de partida', d: 'Entiendes si realmente necesitas un activo, qué papel puede tener y con qué estructura.' },
      { n: '02', icon: MapPin, t: 'Evalúas el proyecto con contexto real', d: 'Ubicación, calidad, destino de uso, gestión y lógica patrimonial.' },
      { n: '03', icon: Check, t: 'Entras solo si tiene sentido para ti', d: 'Primero la estrategia, luego la visita, luego la decisión.' },
    ],
    projectCards: [
      { img: PIC.boulevard, pos: 'center 55%', t: 'Arquitectura contemporánea', d: 'Tres torres, identidad fuerte y presencia icónica.' },
      { img: PIC.terrace, pos: 'center 60%', t: 'Espacios que elevan la experiencia', d: 'Zonas comunes cuidadas, líneas limpias y confort diario.' },
      { img: PIC.rooftop, pos: 'center 45%', t: 'Rooftop con vistas al mar', d: 'Piscinas panorámicas y lifestyle premium.' },
      { img: PIC.towers, pos: 'center 55%', t: 'Un proyecto junto al mar', d: 'Ubicación costera, estética y habitabilidad.' },
    ],
    infraTitle: '¿Por qué partir de una infraestructura patrimonial?',
    infraSubtitle: 'Un inmueble por sí solo no basta. El verdadero valor nace de una visión más amplia.',
    infraImageAlt: 'Three Garden Qerret — vista aérea',
    infraPillars: [
      { icon: Home, t: 'Activo', d: 'El proyecto es solo una parte de la ecuación.' },
      { icon: Settings, t: 'Gestión', d: 'Sin un operador y una lógica de uso, el ladrillo por sí solo no basta.' },
      { icon: BarChart3, t: 'Fiscalidad y flujos', d: 'Estructura, movimientos y planteamiento inciden en el resultado final.' },
      { icon: ShieldCheck, t: 'Protección y visión', d: 'Patrimonialización, continuidad y orden cuentan tanto como la elección del bien.' },
    ],
    zoomTitle: '¿Qué obtienes en la Zoom Estratégica?',
    zoomSubtitle: 'Una primera conversación para entender si Three Garden puede ser una pieza coherente en tu infraestructura patrimonial.',
    zoomCards: [
      { icon: UserRound, t: 'Análisis de tu objetivo', d: 'Entender qué quieres construir realmente.', img: PIC.night, pos: 'center 45%' },
      { icon: Search, t: 'Evaluación del proyecto', d: 'Leer el proyecto de la forma correcta, no solo estéticamente.', img: PIC.facade, pos: 'center 40%' },
      { icon: MapPin, t: 'Contexto territorial', d: 'Qerret, costa albanesa, potencial y lógica de uso.', img: PIC.aerialSunset, pos: 'center 55%' },
      { icon: ListChecks, t: 'Próximos pasos', d: 'Si tiene sentido, se pasa al siguiente funnel y a la visita.', img: PIC.boulevard, pos: 'center 70%' },
    ],
    zoomNoteB: 'La Zoom no es una llamada genérica:', zoomNote: 'sirve para filtrar, orientar y entender si seguir adelante.',
    whyTitle: ['Por qué Three Garden', 'Por qué Qerret'],
    whyText: 'Una ubicación frente al mar en una de las zonas más interesantes de la costa albanesa. Qerret une mar, tranquilidad y un desarrollo constante, con un contexto moderno y un lifestyle contemporáneo. Three Garden ofrece calidad constructiva, arquitectura distintiva y una experiencia residencial pensada para perdurar en el tiempo.',
    whyTiles: [
      { img: PIC.hero, pos: 'center 70%', t: 'Vivir el mar cada día', d: 'Playa, horizonte y luz natural' },
      { img: PIC.pool, pos: 'center 60%', t: 'Rooftop con vistas infinitas', d: 'Relax, bienestar y conexión' },
      { img: PIC.aerialPools, pos: 'center 32%', t: 'Un territorio en crecimiento', d: 'Naturaleza, desarrollo y potencial' },
      { img: PIC.night, pos: 'center 50%', t: 'Un estilo de vida contemporáneo', d: 'Calidad, diseño y habitabilidad' },
    ],
    lifestyle: [
      { icon: Waves, t: 'Vistas al mar', d: 'Acceso directo a la playa' },
      { icon: Droplets, t: 'Piscinas panorámicas', d: 'Rooftop con vistas 360°' },
      { icon: BarChart3, t: 'Diseño premium', d: 'Arquitectura moderna y materiales de calidad' },
      { icon: Sofa, t: 'Amplias terrazas', d: 'Espacios exteriores para disfrutar todo el año' },
      { icon: MapPin, t: 'Ubicación costera', d: 'Tranquilidad, servicios y conexiones internacionales' },
    ],
    finalTitle: ['La elección correcta no parte del encanto.', 'Parte de la estructura.'],
    finalText: 'Si quieres entender si Three Garden tiene realmente sentido en tu recorrido, entra primero en la Zoom Estratégica. Desde ahí sabrás si seguir adelante y cómo.',
    finalPoints: [
      { icon: UserRound, t: 'Análisis inicial personalizado', d: 'sobre tu perfil y tus objetivos' },
      { icon: FileText, t: 'Contexto real, no promesas', d: 'Datos, escenarios y oportunidades concretas' },
      { icon: CheckCircle2, t: 'Acceso al siguiente paso solo si es coherente', d: 'Sin presión, solo método' },
    ],
    faqKicker: 'Respuestas a tus preguntas', faqTitle: 'Preguntas frecuentes',
    faqIntro: 'Las respuestas a las preguntas más habituales para ayudarte a decidir con conocimiento.',
    faq: [
      { q: '¿Qué es exactamente la Zoom Estratégica?', a: 'Es una primera conversación con el equipo de HCF Group: analizamos tu objetivo, leemos Three Garden en el contexto real de Qerret y entendemos si puede ser una pieza coherente en tu infraestructura patrimonial. No es una venta: sirve para filtrar, orientar y decidir si seguir adelante.' },
      { q: '¿La Zoom me obliga a comprar?', a: 'No. Sin obligación de compra. La Zoom Estratégica sirve para decidir con datos y contexto real, no con renders ni promesas. Se pasa a la visita y a los siguientes pasos solo si tiene sentido para ti.' },
      { q: '¿Por qué partir de la estrategia y no del inmueble?', a: 'Porque el valor real nace de la ubicación, la demanda, la calidad del activo, la gestión y la estructura: elementos que hay que leer antes de cualquier decisión. Primero la estrategia, luego la visita, luego la decisión.' },
      { q: '¿Qué significa infraestructura patrimonial?', a: 'Un sistema integrado de activos, governance, gestión y visión a largo plazo que trabaja en el tiempo para tu patrimonio. No la unidad aislada, sino la forma en que se inserta en una estrategia.' },
      { q: '¿Por qué Qerret hoy?', a: 'Frente al mar, accesibilidad, demanda en aumento y una ventana de posicionamiento todavía abierta: un contexto que conviene evaluar con anticipación y método, antes de que el mercado madure más.' },
      { q: '¿Recibiré el brochure de Three Garden?', a: 'Al completar el formulario pasas directamente a elegir fecha y hora de la Zoom Estratégica. El brochure oficial en español te lo entregamos durante el recorrido con nuestro equipo.' },
      { q: '¿Qué costes debo considerar además del precio de la unidad?', a: 'En la Zoom analizamos juntos la estructura completa: precio de la unidad, impuestos y gastos notariales locales, posibles costes de gestión y de mobiliario. No publicamos cifras genéricas: cada partida se lee sobre el dossier actualizado del proyecto y sobre tu perfil. La Zoom y el brochure son gratuitos.' },
      { q: '¿Cómo funciona la gestión del inmueble si no vivo en Albania?', a: 'Para HCF el ladrillo por sí solo no basta: la gestión forma parte de la infraestructura patrimonial. En la Zoom evaluamos la lógica de uso (personal, alquiler, mixto) y las opciones de gestión disponibles para Three Garden, con costes y condiciones a verificar en el dossier y en persona durante el Verification Pass.' },
      { q: '¿Cuáles son los plazos del recorrido?', a: 'Zoom Estratégica: reservable ahora, dura 30 minutos. Verification Pass Albania: fines de semana programados con fechas reales, actualizadas por nuestro sistema (las ves en esta página). Plazos de entrega y avance de Three Garden: los verificas en el dossier y sobre el terreno, no en una promesa.' },
      { q: '¿Qué pasa después de la Zoom?', a: 'Si Three Garden es coherente con tu perfil, el siguiente paso es el Verification Pass Albania: un fin de semana sobre el terreno para ver territorio, proyecto y datos en vivo con el equipo HCF (Private Rate €1.000). Si no es coherente, te lo decimos con claridad. Sin obligación de compra; condiciones y disponibilidad sujetas a verificación.' },
    ],
    footerPrivacy: 'Privacidad', footerCookies: 'Cookies', footerSections: 'Secciones (pie)',
    brochureEmail: false, // ★ brochure ES non ancora disponibile (la invierà il cliente)
    agenda: {
      kicker: 'Agenda de la Zoom Estratégica',
      title: 'Qué ocurre en 30 minutos',
      subtitle: 'Una conversación estructurada, no una llamada de ventas. Sabes de antemano cómo usaremos el tiempo.',
      meta: '30 minutos · en Zoom · con el equipo de HCF Group',
      items: [
        { time: '0–5 min', t: 'Tu punto de partida', d: 'Perfil, objetivo y estructura: entendemos si un activo como Three Garden puede tener un papel en tu recorrido.' },
        { time: '5–15 min', t: 'Three Garden en su contexto real', d: 'Ubicación en Qerret, calidad constructiva, destino de uso, lógica de gestión y patrimonial.' },
        { time: '15–25 min', t: 'Estructura, costes y gestión', d: 'Lo que de verdad hay que considerar: estructura de compra, costes accesorios, gestión y flujos. Números solo de un dossier verificable.' },
        { time: '25–30 min', t: 'Próximos pasos, solo si son coherentes', d: 'Si tiene sentido para ti: acceso al Verification Pass Albania (verificación sobre el terreno). Si no, nos despedimos sin presión.' },
      ],
      note: 'La Zoom es gratuita. Trae tus preguntas sobre costes, gestión y plazos: respuestas documentadas, no diapositivas.',
    },
    video: { kicker: 'Mini vídeo · 45 segundos', title: 'Three Garden en 45 segundos', text: 'El proyecto, la ubicación y el método HCF en menos de un minuto.' },
    proofs: {
      kicker: 'Pruebas, no promesas',
      title: 'Qué puedes verificar antes de decidir',
      intro: 'Cada afirmación de esta página está vinculada a algo que puedes leer, ver o comprobar en persona.',
      items: [
        { icon: FileText, t: 'Brochure oficial Three Garden Residence', d: 'El documento del proyecto: planos, renders y equipamiento. Te lo entregamos durante el recorrido con nuestro equipo.', meta: 'PDF · durante el recorrido' },
        { icon: Video, t: (n) => `${n} testimonios en vídeo`, d: 'Clientes y participantes del Verification Pass Albania cuentan lo que vieron, en vivo.', meta: 'YouTube · en esta página', action: 'testimonianze', actionLabel: 'Ver los testimonios' },
        { icon: CalendarClock, t: 'Fechas reales del Verification Pass Albania', d: 'Fines de semana de verificación sobre el terreno con el equipo HCF. Fechas y plazas actualizadas en tiempo real por nuestro sistema.', dates: true },
        { icon: MessageSquare, t: 'Conversación directa con el equipo', d: 'En la Zoom puedes preguntar lo que quieras sobre costes, gestión y plazos: respuestas documentadas, no diapositivas.', meta: '30 minutos · Zoom' },
      ],
      datesEmpty: 'Nuevas fechas en programación: te las comunicamos en la Zoom.',
      seatsLabel: 'plazas', soldOut: 'Completo',
      footnote: 'Las fechas y plazas mostradas se leen en tiempo real de nuestro sistema. Sin obligación de compra; condiciones y disponibilidad sujetas a verificación.',
    },
    bridge: {
      kicker: 'Después de la Zoom',
      title: ['El siguiente paso es la verificación sobre el terreno:', 'el Verification Pass Albania.'],
      text: 'La Zoom sirve para entender si Three Garden puede tener sentido para ti. Si la respuesta es sí, el recorrido continúa con el Verification Pass: un fin de semana en Albania para ver territorio, proyecto y datos en vivo, con el equipo HCF.',
      steps: [
        { icon: Video, t: 'Zoom Estratégica · 30 min', d: 'Perfil, objetivo y lectura de Three Garden en su contexto real. Gratuita y sin compromiso.', tag: 'Hoy' },
        { icon: MapPin, t: 'Verification Pass Albania', d: 'Fin de semana sobre el terreno: territorio, Three Garden y los activos seleccionados, conversación con el equipo HCF.', tag: 'Private Rate €1.000 · fin de semana' },
        { icon: CheckCircle2, t: 'Decisión con datos verificados', d: 'Solo después de verlo en persona. Primero la estrategia, luego la visita, luego la decisión.', tag: 'Sin presión' },
      ],
      includesTitle: 'Qué incluye el Verification Pass',
      includes: ['Acceso completo a los datos y a la documentación de los activos', 'Análisis del territorio en vivo con el equipo HCF', 'Visita on-site de Three Garden y de las oportunidades seleccionadas', 'Conversación directa sobre costes, gestión y plazos'],
      includesNote: 'Qué incluye y qué no incluye el Pass (viaje, alojamiento, condiciones) se aclara en la Zoom y en la página del Verification Pass.',
      datesTitle: 'Próximas fechas', datesEmpty: 'Nuevas fechas en programación.', datesNote: 'Fechas y plazas actualizadas en tiempo real por nuestro sistema.',
      seatsLabel: 'plazas', soldOut: 'Completo',
      price: 'Private Rate €1.000',
      cta: 'Descubre el Verification Pass Albania',
      href: '/f/es#verification',
      vpSource: 'es',
    },
    dlg: {
      kicker: 'Zoom Estratégica · Three Garden Qerret',
      title: 'Reserva la Zoom Estratégica.',
      descBrochure: <>Déjanos tus datos: recibes <b>ahora mismo por email el brochure Three Garden</b> y pasas a elegir fecha y hora de la Zoom con el <b>equipo de HCF Group</b>.</>,
      desc: <>Déjanos tus datos: justo después pasas a elegir fecha y hora de la Zoom Estratégica con el <b>equipo de HCF Group</b>.</>,
      phName: 'Nombre y apellidos *', phEmail: 'Email *', phPhone: 'Teléfono (recomendado)',
      submit: 'Continuar',
      errName: 'Introduce nombre y apellidos (al menos 2 caracteres).', errEmail: 'Introduce un email válido.',
      legal: 'Al enviar aceptas nuestra', privacy: 'Política de Privacidad',
      steps: ['Contacto', 'Perfil', 'Calendario'],
      pq: {
        kicker: 'Precalificación · 20 segundos',
        title: 'Tres respuestas para preparar una Zoom útil.',
        desc: 'Solo sirven para leer Three Garden sobre tu perfil real. Ninguna respuesta es vinculante.',
        goal: 'Objetivo principal', budget: 'Presupuesto orientativo', timeline: 'Horizonte de decisión',
        submit: 'Continuar a la reserva', back: 'Atrás', skip: 'Prefiero responder en la Zoom',
        note: 'Sin obligación de compra; condiciones y disponibilidad sujetas a verificación.',
      },
      okTitle: 'Solicitud recibida',
      okBrochure: <>El <b>brochure Three Garden</b> está llegando a tu correo. En un instante te llevamos al calendario para elegir <b>fecha y hora de la Zoom Estratégica</b>.</>,
      ok: <>En un instante te llevamos al calendario para elegir <b>fecha y hora de la Zoom Estratégica</b> con el equipo de HCF Group.</>,
      okCta: 'Elige fecha y hora de la Zoom', okNote: 'Redirección automática en curso…',
      bridge: { title: 'Después de la Zoom: Verification Pass Albania', text: 'Si Three Garden es coherente con tu perfil, el siguiente paso es la verificación sobre el terreno (fin de semana · Private Rate €1.000).', cta: 'Descubre el Verification Pass' },
      toastTitle: '¡Solicitud recibida!',
      toastBrochure: 'Brochure en camino por email. Te llevamos a elegir fecha y hora de la Zoom.',
      toast: 'Te llevamos a elegir fecha y hora de la Zoom Estratégica.',
      toastErr: 'No se pudo enviar, inténtalo de nuevo.',
    },
  },
  // ★ Fase 85 — ENGLISH version (EN-AL-TG), twin of ES-AL-TG · leads → Richard
  en: {
    slug: 'en-al-tg', code: 'EN-AL-TG', homeHref: '/f/en', propertySlug: 'en-three-garden-qerret',
    docTitle: 'Three Garden Qerret · Albania — HCF Property',
    docDescription: 'Three Garden Qerret: seafront in Albania, three towers with direct beach access and rooftop pools. Book the Strategic Zoom with HCF Property.',
    leadMessage: 'Strategic Zoom request — Three Garden (Qerret, Albania) · landing EN-AL-TG',
    nav: [
      { id: 'progetto', label: 'Three Garden' },
      { id: 'posizione', label: 'Location' },
      { id: 'agenda', label: 'Zoom agenda' },
      { id: 'percorso', label: 'After the Zoom' },
      { id: 'infrastruttura', label: 'Wealth infrastructure' },
      { id: 'faq', label: 'Frequently asked questions' },
    ],
    menuOpen: 'Open menu', menuClose: 'Close menu', sections: 'Sections',
    cta: 'Book the Strategic Zoom', ctaShort: 'Book the Zoom', ctaAria: 'Book the Strategic Zoom',
    ctaPrimary: 'Book the Strategic Zoom to evaluate Three Garden',
    heroKicker: ['Albania', 'Qerret'],
    heroTitle: 'Three Garden Qerret',
    heroSubtitle: ['Don’t start from the property.', 'Start from the wealth infrastructure.'],
    heroLead: 'Find out whether this project fits your profile, your goal and your structure before making any decision.',
    heroText: 'Three Garden is not just a seafront project. For HCF it is a possible piece of a wealth infrastructure built with logic, cash flows, protection, management and vision.',
    heroSecondary: 'See the project',
    heroImageAlt: 'Three Garden Qerret — view from the beach',
    heroFeatures: [
      { icon: Waves, t: 'Seafront', d: 'Direct beach access' },
      { icon: Droplets, t: 'Panoramic rooftop pools', d: 'Unique views over the coast' },
      { icon: Building2, t: 'Contemporary architecture', d: 'Modern design and iconic identity' },
    ],
    strip: 'For those who want to understand structure, risk, management and potential first. Not for the merely curious.',
    steps: [
      { n: '01', icon: TrendingUp, t: 'You analyse your starting point', d: 'You understand whether you really need an asset, what role it can play and with which structure.' },
      { n: '02', icon: MapPin, t: 'You assess the project in its real context', d: 'Location, quality, intended use, management and wealth logic.' },
      { n: '03', icon: Check, t: 'You enter only if it makes sense for you', d: 'First the strategy, then the visit, then the decision.' },
    ],
    projectCards: [
      { img: PIC.boulevard, pos: 'center 55%', t: 'Contemporary architecture', d: 'Three towers, strong identity and iconic presence.' },
      { img: PIC.terrace, pos: 'center 60%', t: 'Spaces that elevate the experience', d: 'Well-kept common areas, clean lines and everyday comfort.' },
      { img: PIC.rooftop, pos: 'center 45%', t: 'Rooftop with sea views', d: 'Panoramic pools and premium lifestyle.' },
      { img: PIC.towers, pos: 'center 55%', t: 'A project by the sea', d: 'Coastal location, aesthetics and liveability.' },
    ],
    infraTitle: 'Why start from a wealth infrastructure?',
    infraSubtitle: 'A property alone is not enough. Real value is born from a broader vision.',
    infraImageAlt: 'Three Garden Qerret — aerial view',
    infraPillars: [
      { icon: Home, t: 'Asset', d: 'The project is only one part of the equation.' },
      { icon: Settings, t: 'Management', d: 'Without an operator and a logic of use, bricks alone are not enough.' },
      { icon: BarChart3, t: 'Tax and cash flows', d: 'Structure, movements and approach affect the final result.' },
      { icon: ShieldCheck, t: 'Protection and vision', d: 'Wealth structuring, continuity and order matter as much as the choice of the asset.' },
    ],
    zoomTitle: 'What do you get in the Strategic Zoom?',
    zoomSubtitle: 'A first conversation to understand whether Three Garden can be a coherent piece of your wealth infrastructure.',
    zoomCards: [
      { icon: UserRound, t: 'Analysis of your goal', d: 'Understanding what you really want to build.', img: PIC.night, pos: 'center 45%' },
      { icon: Search, t: 'Assessment of the project', d: 'Reading the project the right way, not just aesthetically.', img: PIC.facade, pos: 'center 40%' },
      { icon: MapPin, t: 'Territorial context', d: 'Qerret, the Albanian coast, potential and logic of use.', img: PIC.aerialSunset, pos: 'center 55%' },
      { icon: ListChecks, t: 'Next steps', d: 'If it makes sense, you move on to the next funnel and the visit.', img: PIC.boulevard, pos: 'center 70%' },
    ],
    zoomNoteB: 'The Zoom is not a generic call:', zoomNote: 'it serves to filter, guide and understand whether to go ahead.',
    whyTitle: ['Why Three Garden', 'Why Qerret'],
    whyText: 'A seafront location in one of the most interesting areas of the Albanian coast. Qerret combines sea, tranquillity and steady development, with a modern context and a contemporary lifestyle. Three Garden offers construction quality, distinctive architecture and a residential experience designed to last over time.',
    whyTiles: [
      { img: PIC.hero, pos: 'center 70%', t: 'Living the sea every day', d: 'Beach, horizon and natural light' },
      { img: PIC.pool, pos: 'center 60%', t: 'Rooftop with endless views', d: 'Relaxation, well-being and connection' },
      { img: PIC.aerialPools, pos: 'center 32%', t: 'A growing territory', d: 'Nature, development and potential' },
      { img: PIC.night, pos: 'center 50%', t: 'A contemporary lifestyle', d: 'Quality, design and liveability' },
    ],
    lifestyle: [
      { icon: Waves, t: 'Sea views', d: 'Direct beach access' },
      { icon: Droplets, t: 'Panoramic pools', d: 'Rooftop with 360° views' },
      { icon: BarChart3, t: 'Premium design', d: 'Modern architecture and quality materials' },
      { icon: Sofa, t: 'Wide terraces', d: 'Outdoor spaces to enjoy all year round' },
      { icon: MapPin, t: 'Coastal location', d: 'Tranquillity, services and international connections' },
    ],
    finalTitle: ['The right choice does not start from charm.', 'It starts from structure.'],
    finalText: 'If you want to understand whether Three Garden really makes sense in your journey, start with the Strategic Zoom. From there you will know whether to go ahead and how.',
    finalPoints: [
      { icon: UserRound, t: 'Personalised initial analysis', d: 'of your profile and your goals' },
      { icon: FileText, t: 'Real context, not promises', d: 'Data, scenarios and concrete opportunities' },
      { icon: CheckCircle2, t: 'Access to the next step only if coherent', d: 'No pressure, just method' },
    ],
    faqKicker: 'Answers to your questions', faqTitle: 'Frequently asked questions',
    faqIntro: 'The answers to the most common questions to help you decide with awareness.',
    faq: [
      { q: 'What exactly is the Strategic Zoom?', a: 'It is a first conversation with the HCF Group team: we analyse your goal, read Three Garden in the real context of Qerret and understand whether it can be a coherent piece of your wealth infrastructure. It is not a sale: it serves to filter, guide and decide whether to go ahead.' },
      { q: 'Does the Zoom oblige me to buy?', a: 'No. No obligation to buy. The Strategic Zoom is there to decide with data and real context, not with renders or promises. You move on to the visit and the next steps only if it makes sense for you.' },
      { q: 'Why start from the strategy and not from the property?', a: 'Because real value is born from location, demand, asset quality, management and structure: elements to be read before any decision. First the strategy, then the visit, then the decision.' },
      { q: 'What does wealth infrastructure mean?', a: 'An integrated system of assets, governance, management and long-term vision that works over time for your wealth. Not the isolated unit, but the way it fits into a strategy.' },
      { q: 'Why Qerret today?', a: 'Seafront, accessibility, rising demand and a positioning window still open: a context worth assessing with anticipation and method, before the market matures further.' },
      { q: 'Will I receive the Three Garden brochure?', a: 'Once you complete the form you go straight to choosing the date and time of the Strategic Zoom. The official brochure in English is handed over during the journey with our team.' },
      { q: 'Which costs should I consider beyond the unit price?', a: 'In the Zoom we go through the complete structure together: unit price, local taxes and notary fees, possible management and furnishing costs. We do not publish generic figures: every item is read on the up-to-date project dossier and on your profile. The Zoom and the brochure are free.' },
      { q: 'How does property management work if I do not live in Albania?', a: 'For HCF bricks alone are not enough: management is part of the wealth infrastructure. In the Zoom we assess the logic of use (personal, rental, mixed) and the management options available for Three Garden, with costs and terms to be verified on the dossier and in person during the Verification Pass.' },
      { q: 'What are the timelines of the journey?', a: 'Strategic Zoom: bookable now, 30 minutes. Verification Pass Albania: scheduled weekends with real dates, updated by our system (you can see them on this page). Delivery times and construction progress of Three Garden: you verify them on the dossier and on site, not on a promise.' },
      { q: 'What happens after the Zoom?', a: 'If Three Garden is coherent with your profile, the next step is the Verification Pass Albania: a weekend on site to see the territory, the project and the data live with the HCF team (Private Rate €1,000). If it is not coherent, we tell you clearly. No obligation to buy; terms and availability subject to verification.' },
    ],
    footerPrivacy: 'Privacy', footerCookies: 'Cookies', footerSections: 'Sections (footer)',
    brochureEmail: false, // ★ EN brochure not yet available (the client will send it)
    agenda: {
      kicker: 'Strategic Zoom agenda',
      title: 'What happens in 30 minutes',
      subtitle: 'A structured conversation, not a sales call. You know in advance how we will use the time.',
      meta: '30 minutes · on Zoom · with Richard Demichele (CEO, HCF Group)',
      items: [
        { time: '0–5 min', t: 'Your starting point', d: 'Profile, goal and structure: we understand whether and what role an asset like Three Garden can play in your journey.' },
        { time: '5–15 min', t: 'Three Garden in its real context', d: 'Location in Qerret, construction quality, intended use, management and wealth logic.' },
        { time: '15–25 min', t: 'Structure, costs and management', d: 'What really matters: purchase structure, ancillary costs, management and cash flows. Numbers only from a verifiable dossier.' },
        { time: '25–30 min', t: 'Next steps, only if coherent', d: 'If it makes sense for you: access to the Verification Pass Albania (on-site verification). Otherwise we part ways, no pressure.' },
      ],
      note: 'The Zoom is free. Bring your questions on costs, management and timelines: documented answers, not slides.',
    },
    video: { kicker: 'Mini video · 45 seconds', title: 'Three Garden in 45 seconds', text: 'The project, the location and the HCF method in less than a minute.' },
    proofs: {
      kicker: 'Proof, not promises',
      title: 'What you can verify before deciding',
      intro: 'Every statement on this page is linked to something you can read, watch or see in person.',
      items: [
        { icon: FileText, t: 'Official Three Garden Residence brochure', d: 'The project document: floor plans, renders and features. Handed over during the journey with our team.', meta: 'PDF · during the journey' },
        { icon: Video, t: (n) => `${n} video testimonials`, d: 'Clients and Verification Pass Albania participants tell what they saw, live.', meta: 'YouTube · on this page', action: 'testimonianze', actionLabel: 'Watch the testimonials' },
        { icon: CalendarClock, t: 'Real Verification Pass Albania dates', d: 'On-site verification weekends with the HCF team. Dates and seats updated in real time by our system.', dates: true },
        { icon: MessageSquare, t: 'Direct conversation with the CEO', d: 'In the Zoom you can ask anything about costs, management and timelines: documented answers, not slides.', meta: '30 minutes · Zoom' },
      ],
      datesEmpty: 'New dates being scheduled: we share them in the Zoom.',
      seatsLabel: 'seats', soldOut: 'Sold out',
      footnote: 'Dates and seats shown are read in real time from our system. No obligation to buy; terms and availability subject to verification.',
    },
    bridge: {
      kicker: 'After the Zoom',
      title: ['The next step is on-site verification:', 'the Verification Pass Albania.'],
      text: 'The Zoom is there to understand whether Three Garden can make sense for you. If the answer is yes, the journey continues with the Verification Pass: a weekend in Albania to see the territory, the project and the data live, with the HCF team.',
      steps: [
        { icon: Video, t: 'Strategic Zoom · 30 min', d: 'Profile, goal and reading of Three Garden in its real context. Free and without obligation.', tag: 'Today' },
        { icon: MapPin, t: 'Verification Pass Albania', d: 'Weekend on site: territory, Three Garden and the selected assets, conversation with the HCF team.', tag: 'Private Rate €1,000 · weekend' },
        { icon: CheckCircle2, t: 'Decision with verified data', d: 'Only after seeing it in person. First the strategy, then the visit, then the decision.', tag: 'No pressure' },
      ],
      includesTitle: 'What the Verification Pass includes',
      includes: ['Full access to the data and documentation of the assets', 'Live territory analysis with the HCF team', 'On-site visit of Three Garden and the selected opportunities', 'Direct conversation on costs, management and timelines'],
      includesNote: 'What the Pass includes and does not include (travel, accommodation, terms) is clarified in the Zoom and on the Verification Pass page.',
      datesTitle: 'Upcoming dates', datesEmpty: 'New dates being scheduled.', datesNote: 'Dates and seats updated in real time by our system.',
      seatsLabel: 'seats', soldOut: 'Sold out',
      price: 'Private Rate €1,000',
      cta: 'Discover the Verification Pass Albania',
      href: '/f/en#verification',
      vpSource: 'en',
    },
    dlg: {
      kicker: 'Strategic Zoom · Three Garden Qerret',
      title: 'Book the Strategic Zoom.',
      descBrochure: <>Leave us your details: you receive <b>the Three Garden brochure by email right away</b> and go on to choose the date and time of the Zoom with <b>Richard Demichele (CEO)</b>.</>,
      desc: <>Leave us your details: right after, you go on to choose the date and time of the Strategic Zoom with <b>Richard Demichele (CEO of HCF Group)</b>.</>,
      phName: 'Full name *', phEmail: 'Email *', phPhone: 'Phone (recommended)',
      submit: 'Continue',
      errName: 'Enter your full name (at least 2 characters).', errEmail: 'Enter a valid email address.',
      legal: 'By submitting you accept our', privacy: 'Privacy Policy',
      steps: ['Contact', 'Profile', 'Calendar'],
      pq: {
        kicker: 'Pre-qualification · 20 seconds',
        title: 'Three answers to prepare a useful Zoom.',
        desc: 'They only help us read Three Garden against your real profile. No answer is binding.',
        goal: 'Main goal', budget: 'Indicative budget', timeline: 'Decision horizon',
        submit: 'Continue to booking', back: 'Back', skip: 'I prefer to answer in the Zoom',
        note: 'No obligation to buy; terms and availability subject to verification.',
      },
      okTitle: 'Request received',
      okBrochure: <>The <b>Three Garden brochure</b> is on its way to your inbox. In a moment we take you to the calendar to choose the <b>date and time of the Strategic Zoom</b>.</>,
      ok: <>In a moment we take you to the calendar to choose the <b>date and time of the Strategic Zoom</b> with Richard Demichele (CEO).</>,
      okCta: 'Choose the date and time of the Zoom', okNote: 'Automatic redirect in progress…',
      bridge: { title: 'After the Zoom: Verification Pass Albania', text: 'If Three Garden is coherent with your profile, the next step is on-site verification (weekend · Private Rate €1,000).', cta: 'Discover the Verification Pass' },
      toastTitle: 'Request received!',
      toastBrochure: 'Brochure on its way by email. We take you to choose the date and time of the Zoom.',
      toast: 'We take you to choose the date and time of the Strategic Zoom.',
      toastErr: 'Could not send, please try again.',
    },
  },
};

/* ── UI atoms ── */
function PrimaryBtn({ children, onClick, testId, className = '', type = 'button', disabled = false, href }) {
  const cls = `tgl-btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 h-12 text-[13px] sm:text-sm font-sans font-semibold ${className}`;
  if (href) {
    return (
      <a href={href} className={cls} data-testid={testId} onClick={onClick}>{children}</a>
    );
  }
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={cls} data-testid={testId}>
      {children}
    </button>
  );
}

const SectionTitle = ({ children, className = '', testId, light = false }) => (
  <h2
    className={`font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.08] tracking-tight ${className}`}
    style={{ color: light ? '#FFFBF3' : INK }}
    data-testid={testId}
  >
    {children}
  </h2>
);

function ArrowBtn({ onClick, label, testId }) {
  return (
    <button type="button" onClick={onClick} className="tgl-arrow shrink-0" aria-label={label} data-testid={testId}>
      <ArrowRight className="w-4 h-4" />
    </button>
  );
}

/* ── Form lead: ★ Fase 94 → ZoomLeadDialog (3 step) in components/funnels/ZoomLeadDialog.jsx ── */

/* ── Pagina ── */
export default function ThreeGardenLanding({ lang = 'it' }) {
  const c = CONTENT[lang] || CONTENT.it;
  const [formOpen, setFormOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingUrl, setBookingUrl] = useState(CALENDLY_CEO);
  // ★ Fase 94 (Doc 02) — date Verification Pass live dal CRM (nessuna data inventata)
  const { dates: vpDates } = useVpDates(c.bridge?.vpSource);

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    // ★ Fase 89 — meta social: thumbnail = card Three Garden, og:url = link specifico
    setPageMeta({
      title: c.docTitle,
      description: c.docDescription,
      image: ogImageFor(c.slug),
      imageAlt: c.heroImageAlt,
      path: `/f/${c.slug}`,
      locale: lang,
    });
    captureAttribution(); // ★ Fase 91/94 — utm_*, click_id, first_touch, landing_path preservati
    window.scrollTo(0, 0);
    api.get(`/public/funnel/${c.slug}`)
      .then((r) => { if (r.data?.booking_url) setBookingUrl(r.data.booking_url); })
      .catch(() => {});
  }, [c, lang]);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // ★ Fase 94 — ogni CTA verso il form traccia `cta_click` (solo con consenso analytics)
  const openForm = (label) => {
    setMenuOpen(false);
    trackFunnelEvent('cta_click', { funnel: c.slug, label: typeof label === 'string' ? label : c.ctaPrimary, target: 'form' });
    setFormOpen(true);
  };

  // ★ Fase 94 — prove: risolve titoli dinamici (conteggio testimonianze) e azioni (scroll)
  const proofs = c.proofs ? {
    ...c.proofs,
    items: c.proofs.items.map((p) => ({
      ...p,
      t: typeof p.t === 'function' ? p.t(HCF_TESTIMONIALS.length) : p.t,
      action: p.action ? { label: p.actionLabel, onClick: () => scrollTo(p.action) } : undefined,
    })),
  } : null;
  const miniVideo = TG_MINI_VIDEO[lang] ? { id: TG_MINI_VIDEO[lang], ...c.video } : null;

  return (
    <div className="relative min-h-screen font-sans" style={{ background: IVORY, color: INK }} data-testid="three-garden-landing" data-lang={lang}>
      <style>{CSS}</style>

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 border-b" style={{ background: 'rgba(249,246,240,0.92)', backdropFilter: 'blur(14px)', borderColor: LINE }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to={c.homeHref} className="shrink-0" data-testid="tgl-logo-home" aria-label="HCF Property">
            <img src="/funnels/hcf-logo-dark.png" alt="HCF Property" className="h-9 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7 text-[13px] font-medium" aria-label={c.sections}>
            {c.nav.map((n) => (
              <button key={n.id} type="button" onClick={() => scrollTo(n.id)} className="tgl-nav-link" data-testid={`tgl-nav-${n.id}`}>{n.label}</button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <PrimaryBtn onClick={openForm} className="!h-10 !px-4 sm:!px-5 !text-[12px] sm:!text-[13px]" testId="tgl-header-cta">
              <span className="hidden sm:inline">{c.cta}</span>
              <span className="sm:hidden">{c.ctaShort}</span>
              <ArrowRight className="w-4 h-4" />
            </PrimaryBtn>
            <button
              type="button" onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-xl grid place-items-center border transition-colors hover:bg-black/5"
              style={{ borderColor: LINE, color: INK }}
              aria-label={menuOpen ? c.menuClose : c.menuOpen} aria-expanded={menuOpen}
              data-testid="tgl-menu-toggle"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="lg:hidden border-t px-4 py-3 flex flex-col" style={{ borderColor: LINE, background: IVORY }} aria-label={`${c.sections} (mobile)`} data-testid="tgl-mobile-menu">
            {c.nav.map((n) => (
              <button key={n.id} type="button" onClick={() => scrollTo(n.id)} className="text-left py-3 text-[15px] font-medium border-b last:border-b-0" style={{ borderColor: LINE, color: INK }} data-testid={`tgl-mnav-${n.id}`}>{n.label}</button>
            ))}
          </nav>
        )}
      </header>

      {/* ── HERO ── */}
      <section id="top" className="relative overflow-hidden" data-testid="tgl-hero">
        {/* Immagine full-bleed (desktop) con fade verso l'avorio sul lato testo */}
        <div className="absolute inset-0 hidden lg:block" aria-hidden="true">
          <img src={PIC.hero} alt="" className="absolute inset-y-0 right-0 h-full w-[68%] object-cover" style={{ objectPosition: 'center 62%' }} fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${IVORY} 0%, ${IVORY} 34%, rgba(249,246,240,0.92) 42%, rgba(249,246,240,0.55) 50%, rgba(249,246,240,0) 60%)` }} />
          <div className="absolute inset-x-0 bottom-0 h-28" style={{ background: `linear-gradient(180deg, rgba(249,246,240,0) 0%, ${IVORY} 100%)` }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10 sm:pt-14 lg:pt-20 lg:pb-24 lg:min-h-[680px] flex flex-col justify-center">
          {/* Immagine (mobile / tablet) */}
          <div className="lg:hidden -mx-4 sm:mx-0 sm:rounded-3xl overflow-hidden aspect-[16/11] mb-8" data-testid="tgl-hero-image-mobile">
            <img src={PIC.hero} alt={c.heroImageAlt} className="w-full h-full object-cover" style={{ objectPosition: 'center 60%' }} fetchPriority="high" />
          </div>

          <div className="max-w-[640px]">
            <div className="text-[11px] sm:text-xs font-semibold tracking-[0.34em] uppercase" style={{ color: INK_SOFT }} data-testid="tgl-hero-kicker">
              {c.heroKicker[0]} <span className="mx-2" style={{ color: OLIVE }}>•</span> {c.heroKicker[1]}
            </div>
            <h1 className="mt-3 font-serif text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[4.4rem] tracking-tight" style={{ color: INK }} data-testid="tgl-hero-title">
              {c.heroTitle}
            </h1>
            <p className="mt-3 font-serif text-[1.45rem] sm:text-[1.9rem] lg:text-[2.15rem] leading-[1.12] tracking-tight" style={{ color: INK }} data-testid="tgl-hero-subtitle">
              {c.heroSubtitle[0]}<br />{c.heroSubtitle[1]}
            </p>
            <p className="mt-5 text-[16px] sm:text-[19px] leading-relaxed" style={{ color: INK }} data-testid="tgl-hero-lead">
              {c.heroLead}
            </p>
            <p className="mt-4 text-[14px] sm:text-[15px] leading-relaxed" style={{ color: INK_SOFT }} data-testid="tgl-hero-text">
              {c.heroText}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <PrimaryBtn onClick={() => openForm(c.ctaPrimary)} testId="tgl-hero-cta" className="w-full sm:w-auto !h-[3.25rem] !px-7 text-left">
                {c.ctaPrimary} <ArrowRight className="w-4 h-4 shrink-0" />
              </PrimaryBtn>
              <button
                type="button" onClick={() => scrollTo('progetto')}
                className="tgl-btn-outline inline-flex items-center justify-center gap-2 rounded-xl px-6 h-[3.25rem] text-[13px] sm:text-sm font-semibold w-full sm:w-auto"
                data-testid="tgl-hero-secondary"
              >
                <Play className="w-4 h-4" style={{ color: OLIVE }} /> {c.heroSecondary}
              </button>
            </div>
            {/* ★ Fase 94 (Doc 02) — microcopy obbligatoria accanto alla CTA primaria */}
            <NoObligationNote lang={lang} theme="light" className="mt-3 !justify-start" testId="tgl-hero-no-obligation" compact />

            <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 sm:divide-x" style={{ borderColor: LINE }} data-testid="tgl-hero-features">
              {c.heroFeatures.map((f, i) => (
                <div key={f.t} className={`flex items-start gap-3 ${i > 0 ? 'sm:pl-6' : ''}`} data-testid={`tgl-hero-feature-${i + 1}`}>
                  <f.icon className="w-7 h-7 shrink-0 mt-0.5" style={{ color: OLIVE }} strokeWidth={1.4} />
                  <div>
                    <div className="font-serif text-[16px] leading-tight" style={{ color: INK }}>{f.t}</div>
                    <p className="mt-1 text-[12.5px] leading-snug" style={{ color: INK_SOFT }}>{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Strip ── */}
      <section className="border-y" style={{ background: IVORY, borderColor: LINE }} data-testid="tgl-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-[10.5px] sm:text-[11.5px] font-semibold tracking-[0.26em] uppercase leading-relaxed" style={{ color: OLIVE }}>
            {c.strip}
          </p>
        </div>
      </section>

      {/* ── 3 step ── */}
      <section className="relative" style={{ background: IVORY_2 }} data-testid="tgl-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="rounded-2xl border grid grid-cols-1 lg:grid-cols-3 lg:divide-x" style={{ background: PAPER, borderColor: LINE }}>
            {c.steps.map((s, i) => (
              <div key={s.n} className={`flex items-start gap-4 p-6 sm:p-7 ${i > 0 ? 'border-t lg:border-t-0' : ''}`} style={{ borderColor: LINE }} data-testid={`tgl-step-${i + 1}`}>
                <span className="w-11 h-11 rounded-full border grid place-items-center shrink-0 font-serif text-[15px]" style={{ borderColor: OLIVE, color: OLIVE }}>{s.n}</span>
                <span className="w-11 h-11 rounded-full border grid place-items-center shrink-0" style={{ borderColor: OLIVE }}>
                  <s.icon className="w-5 h-5" style={{ color: OLIVE }} strokeWidth={1.7} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-serif text-[18px] sm:text-[19px] leading-tight" style={{ color: INK }}>{s.t}</h3>
                  <p className="mt-1.5 text-[13.5px] leading-relaxed" style={{ color: INK_SOFT }}>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4 card progetto ── */}
      <section id="progetto" className="relative scroll-mt-16" style={{ background: IVORY_2 }} data-testid="tgl-project">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {c.projectCards.map((k, i) => (
              <article key={k.t} className="tgl-card rounded-2xl overflow-hidden flex flex-col" data-testid={`tgl-project-card-${i + 1}`}>
                <div className="p-2.5 pb-0">
                  <div className="rounded-xl overflow-hidden aspect-[4/3]">
                    <img src={k.img} alt={k.t} className="w-full h-full object-cover" style={{ objectPosition: k.pos }} loading="lazy" />
                  </div>
                </div>
                <div className="p-5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-serif text-[18px] leading-tight" style={{ color: INK }}>{k.t}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: INK_SOFT }}>{k.d}</p>
                  </div>
                  <ArrowBtn onClick={openForm} label={`${c.ctaAria} — ${k.t}`} testId={`tgl-project-card-${i + 1}-cta`} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Perché partire da un'infrastruttura patrimoniale ── */}
      <section id="infrastruttura" className="relative scroll-mt-16" style={{ background: IVORY }} data-testid="tgl-infra">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,300px)_1fr] gap-8 lg:gap-12 items-stretch">
            <div className="rounded-3xl overflow-hidden aspect-[4/5] lg:aspect-auto order-2 lg:order-1 border" style={{ borderColor: LINE }}>
              <img src={PIC.aerialPools} alt={c.infraImageAlt} className="w-full h-full object-cover" style={{ objectPosition: 'center 60%' }} loading="lazy" />
            </div>
            <div className="order-1 lg:order-2 flex flex-col justify-center">
              <SectionTitle className="text-center lg:text-left" testId="tgl-infra-title">{c.infraTitle}</SectionTitle>
              <p className="mt-4 text-[16px] sm:text-[19px] leading-relaxed text-center lg:text-left" style={{ color: INK_SOFT }} data-testid="tgl-infra-subtitle">
                {c.infraSubtitle}
              </p>
              <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {c.infraPillars.map((p, i) => (
                  <div key={p.t} className="tgl-card rounded-2xl p-5" data-testid={`tgl-pillar-${i + 1}`}>
                    <p.icon className="w-8 h-8" style={{ color: OLIVE }} strokeWidth={1.4} />
                    <div className="mt-4 font-serif text-[18px] leading-tight" style={{ color: INK }}>{p.t}</div>
                    <p className="mt-2 text-[13px] leading-relaxed" style={{ color: INK_SOFT }}>{p.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cosa ottieni nella Zoom Strategica ── */}
      <section id="zoom" className="relative scroll-mt-16" style={{ background: IVORY_2 }} data-testid="tgl-zoom">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <SectionTitle testId="tgl-zoom-title">{c.zoomTitle}</SectionTitle>
            <p className="mt-4 text-[15px] sm:text-[17px] leading-relaxed" style={{ color: INK_SOFT }} data-testid="tgl-zoom-subtitle">
              {c.zoomSubtitle}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {c.zoomCards.map((z, i) => (
              <article key={z.t} className="tgl-card rounded-2xl overflow-hidden flex flex-col" data-testid={`tgl-zoom-card-${i + 1}`}>
                <div className="p-5 flex items-start gap-3">
                  <z.icon className="w-7 h-7 shrink-0 mt-0.5" style={{ color: OLIVE }} strokeWidth={1.5} />
                  <div className="min-w-0">
                    <h3 className="font-serif text-[17px] leading-tight" style={{ color: INK }}>{z.t}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: INK_SOFT }}>{z.d}</p>
                  </div>
                </div>
                <div className="relative mt-auto mx-2.5 mb-2.5 rounded-xl overflow-hidden aspect-[16/10]">
                  <img src={z.img} alt="" className="w-full h-full object-cover" style={{ objectPosition: z.pos }} loading="lazy" />
                  <div className="absolute bottom-2.5 right-2.5">
                    <ArrowBtn onClick={openForm} label={`${c.ctaAria} — ${z.t}`} testId={`tgl-zoom-card-${i + 1}-cta`} />
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-6 rounded-2xl px-5 py-4 flex items-start sm:items-center justify-center gap-3" style={{ background: SAGE }} data-testid="tgl-zoom-note">
            <Info className="w-5 h-5 shrink-0" style={{ color: OLIVE }} strokeWidth={1.8} />
            <p className="text-[13.5px] sm:text-[14px] leading-relaxed" style={{ color: INK_SOFT }}>
              <b style={{ color: INK }}>{c.zoomNoteB}</b> {c.zoomNote}
            </p>
          </div>
        </div>
      </section>

      {/* ── ★ Fase 94 (Doc 02) — Agenda della Zoom (30 min) + slot mini video 45s (solo se configurato) ── */}
      <ZoomAgendaSection
        c={{ ...c.agenda, funnel: c.slug }}
        tokens={TOKENS}
        video={miniVideo}
        onCta={openForm}
        ctaLabel={c.ctaPrimary}
        lang={lang}
        testIdPrefix="tgl-agenda"
        id="agenda"
      />

      {/* ── ★ Fase 94 (Doc 02) — Bridge esplicito: Zoom → Verification Pass Albania ── */}
      <VerificationPassBridge
        c={c.bridge}
        tokens={TOKENS}
        vpDates={vpDates}
        onPrimary={openForm}
        primaryLabel={c.cta}
        funnel={c.slug}
        lang={lang}
        testIdPrefix="tgl-bridge"
        id="percorso"
        image={{ src: PIC.aerialPools, pos: 'center 40%' }}
      />

      {/* ★ Fase 68/69 — video racconto Verification Pass Albania */}
      <section className="relative px-4 sm:px-6 lg:px-8 py-14 sm:py-16" style={{ background: IVORY }}>
        <VpRecapVideo lang={lang} accent={OLIVE} testId="aml-vp-video" />
      </section>

      {/* ── Perché Three Garden / Perché Qerret ── */}
      <section id="posizione" className="relative scroll-mt-16" style={{ background: IVORY }} data-testid="tgl-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <SectionTitle testId="tgl-why-title">{c.whyTitle[0]}<br />{c.whyTitle[1]}</SectionTitle>
            <p className="mt-5 text-[15px] sm:text-[16px] leading-relaxed" style={{ color: INK_SOFT }} data-testid="tgl-why-text">
              {c.whyText}
            </p>
            <div className="mt-7">
              <PrimaryBtn onClick={openForm} testId="tgl-why-cta" className="w-full sm:w-auto">
                {c.cta} <ArrowRight className="w-4 h-4" />
              </PrimaryBtn>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {c.whyTiles.map((t, i) => (
              <figure key={t.t} className="tgl-tile relative rounded-2xl overflow-hidden aspect-[16/10] border" style={{ borderColor: LINE }} data-testid={`tgl-why-tile-${i + 1}`}>
                <img src={t.img} alt={t.t} className="w-full h-full object-cover" style={{ objectPosition: t.pos }} loading="lazy" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(14,20,18,0) 40%, rgba(14,20,18,0.72) 100%)' }} />
                <figcaption className="absolute left-4 right-4 bottom-4">
                  <div className="font-serif text-[17px] leading-tight" style={{ color: '#FFFBF3' }}>{t.t}</div>
                  <div className="mt-0.5 text-[12.5px]" style={{ color: '#E4DED2' }}>{t.d}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ── Strip stile di vita ── */}
      <section id="stile-di-vita" className="relative scroll-mt-16 border-y" style={{ background: IVORY_2, borderColor: LINE }} data-testid="tgl-lifestyle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5 xl:gap-0 xl:divide-x" style={{ borderColor: LINE }}>
          {c.lifestyle.map((l, i) => (
            <div key={l.t} className="flex items-start gap-3 xl:px-6 first:xl:pl-0 last:xl:pr-0" data-testid={`tgl-lifestyle-${i + 1}`}>
              <l.icon className="w-7 h-7 shrink-0" style={{ color: OLIVE }} strokeWidth={1.4} />
              <div>
                <div className="font-serif text-[16px] leading-tight" style={{ color: INK }}>{l.t}</div>
                <p className="mt-1 text-[12.5px] leading-snug" style={{ color: INK_SOFT }}>{l.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ★ Fase 94 (Doc 02) — Prove verificabili (brochure, testimonianze, date Pass live, confronto CEO) ── */}
      <ProofsSection c={proofs} tokens={TOKENS} vpDates={vpDates} testIdPrefix="tgl-proofs" id="prove" />

      {/* ── CTA finale ── */}
      <section className="relative overflow-hidden" data-testid="tgl-final">
        <div className="absolute inset-0">
          <img src={PIC.aerialSunset} alt="" className="w-full h-full object-cover" style={{ objectPosition: 'center 45%' }} loading="lazy" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(14,20,18,0.86) 0%, rgba(14,20,18,0.62) 48%, rgba(14,20,18,0.42) 100%)' }} />
          <div className="tgl-grain" aria-hidden="true" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,400px)] gap-10 items-center">
          <div className="max-w-2xl">
            <h2 className="font-serif text-[2rem] sm:text-5xl leading-[1.08] tracking-tight" style={{ color: '#FFFBF3' }} data-testid="tgl-final-title">
              {c.finalTitle[0]}<br />{c.finalTitle[1]}
            </h2>
            <p className="mt-5 text-[15px] sm:text-lg leading-relaxed max-w-xl" style={{ color: '#E6E1D6' }} data-testid="tgl-final-text">
              {c.finalText}
            </p>
            <div className="mt-7">
              <PrimaryBtn onClick={() => openForm(c.ctaPrimary)} testId="tgl-final-cta" className="w-full sm:w-auto !h-[3.4rem] !px-7 text-left">
                {c.ctaPrimary} <ArrowRight className="w-4 h-4 shrink-0" />
              </PrimaryBtn>
            </div>
            <NoObligationNote lang={lang} theme="dark" color="#D9D2C5" iconColor="#FFFBF3" className="mt-3 !justify-start" testId="tgl-final-no-obligation" compact />
          </div>
          <div className="rounded-2xl border p-5 sm:p-6 space-y-5" style={{ background: 'rgba(255,251,243,0.12)', borderColor: 'rgba(255,255,255,0.28)', backdropFilter: 'blur(16px)' }} data-testid="tgl-final-points">
            {c.finalPoints.map((p, i) => (
              <div key={p.t} className="flex items-start gap-3.5" data-testid={`tgl-final-point-${i + 1}`}>
                <span className="w-10 h-10 rounded-full border grid place-items-center shrink-0" style={{ borderColor: 'rgba(255,255,255,0.45)' }}>
                  <p.icon className="w-5 h-5" style={{ color: '#FFFBF3' }} strokeWidth={1.6} />
                </span>
                <div>
                  <div className="font-serif text-[16px] leading-snug" style={{ color: '#FFFBF3' }}>{p.t}</div>
                  <p className="mt-0.5 text-[12.5px] leading-snug" style={{ color: '#D9D2C5' }}>{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonianze video (fonte unica lib/testimonials.js) ── */}
      <TestimonialsSection lang={lang} theme="light" testIdPrefix="tgl" id="testimonianze" />

      {/* ── FAQ ── */}
      <section id="faq" className="relative scroll-mt-16" style={{ background: IVORY_2 }} data-testid="tgl-faq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr] gap-8 lg:gap-14">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.28em] uppercase" style={{ color: OLIVE }}>{c.faqKicker}</div>
            <SectionTitle className="mt-3" testId="tgl-faq-title">{c.faqTitle}</SectionTitle>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: INK_SOFT }}>
              {c.faqIntro}
            </p>
            <div className="mt-8 rounded-2xl overflow-hidden aspect-[16/10] hidden lg:block border" style={{ borderColor: LINE }}>
              <img src={PIC.facade} alt="" className="w-full h-full object-cover" style={{ objectPosition: 'center 40%' }} loading="lazy" />
            </div>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {c.faq.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="tgl-accordion-item" data-testid={`tgl-faq-item-${i + 1}`}>
                <AccordionTrigger className="font-serif text-left text-[16px] sm:text-[17px] py-4 hover:no-underline" style={{ color: INK }}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[14px] leading-relaxed pb-5" style={{ color: INK_SOFT }}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t" style={{ background: '#0E1512', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-8 w-auto" />
            <p className="text-[11px] mt-2" style={{ color: '#9AA39E' }}>© {new Date().getFullYear()} HCF Property · Three Garden Qerret</p>
          </div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2 text-[12px]" style={{ color: '#C9C2B5' }} aria-label={c.footerSections}>
            {c.nav.map((n) => (
              <button key={n.id} type="button" onClick={() => scrollTo(n.id)} className="hover:text-white transition-colors" data-testid={`tgl-fnav-${n.id}`}>{n.label}</button>
            ))}
          </nav>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[12px] items-center" style={{ color: '#C9C2B5' }}>
            <Link to="/privacy" className="underline hover:text-white" data-testid="tgl-footer-privacy">{c.footerPrivacy}</Link>
            <Link to="/cookie-policy" className="underline hover:text-white" data-testid="tgl-footer-cookies">{c.footerCookies}</Link>
            <span className="tracking-[0.25em]" data-testid="tgl-footer-code">{c.code}</span>
          </div>
        </div>
      </footer>

      {/* ── Mobile sticky CTA ── */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 p-3" style={{ background: 'linear-gradient(180deg, rgba(249,246,240,0) 0%, rgba(249,246,240,0.96) 40%)' }}>
        <PrimaryBtn onClick={openForm} className="w-full shadow-xl" testId="tgl-sticky-cta">
          {c.cta} <ArrowRight className="w-4 h-4" />
        </PrimaryBtn>
      </div>
      <div className="lg:hidden h-20" aria-hidden="true" />

      <ZoomLeadDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        bookingUrl={bookingUrl}
        funnel={c.slug}
        propertySlug={c.propertySlug}
        leadMessage={c.leadMessage}
        lang={lang}
        brochureEmail={c.brochureEmail}
        t={c.dlg}
        tokens={TOKENS}
        testIdPrefix="tgl"
        intent="zoom"
        bridge={c.dlg.bridge ? { ...c.dlg.bridge, href: c.bridge?.href } : null}
      />
    </div>
  );
}
